import React from "react";
import { initialsFrom } from "./tenantOverviewData";

/**
 * One person in a tenancy row: initials avatar, name, contact lines, role
 * badge — the two-person block of the reference layout.
 *
 * Contact lines render only when the field exists. Both an e-mail and a phone
 * now reach this cell, but either can be missing on a given person; an
 * always-present line holding an em dash would read as "this person has no
 * phone", which is a claim the data does not make.
 *
 * The avatar hue is derived from the person's own id, not from the row index.
 * Row-index colouring makes the same person change colour when a filter
 * reorders the list, which quietly undermines the one job the colour has.
 *
 * With `onOpen`, the name becomes its own control that opens THAT person. The
 * cell sits inside a row which navigates to the tenancy's primary tenant, so
 * without this a click on the co-tenant opened somebody else — the row handler
 * is the only one that ever fired. The button stops the click from reaching the
 * row, so exactly one navigation happens and it is the one that was clicked.
 */

const AVATAR_TONES = [
  "border-sky-500/30 bg-sky-500/10 text-sky-700 dark:text-sky-300",
  "border-violet-500/30 bg-violet-500/10 text-violet-700 dark:text-violet-300",
  "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300",
  "border-rose-500/30 bg-rose-500/10 text-rose-700 dark:text-rose-300",
];

function toneFor(seed) {
  const s = String(seed || "");
  let acc = 0;
  for (let i = 0; i < s.length; i += 1) acc = (acc + s.charCodeAt(i)) % 997;
  return AVATAR_TONES[acc % AVATAR_TONES.length];
}

const ROLE_BADGE = {
  Hauptmieter:
    "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  "Co-Mieter":
    "border-sky-500/30 bg-sky-500/10 text-sky-700 dark:text-sky-300",
  Solidarhafter:
    "border-violet-500/30 bg-violet-500/10 text-violet-700 dark:text-violet-300",
};

export function RoleBadge({ label }) {
  if (!label) return null;
  const tone = ROLE_BADGE[label] || "border-border bg-muted text-muted-foreground";
  return (
    <span
      className={`inline-flex rounded-full border px-[7px] py-[1px] text-[10px] font-medium ${tone}`}
    >
      {label}
    </span>
  );
}

/** Placeholder that keeps the column occupied when a tenancy has no co-tenant. */
export function EmptyPersonCell({ note = "Kein Co-Mieter" }) {
  return (
    <div className="flex items-start gap-[10px]">
      <span
        aria-hidden="true"
        className="flex h-[32px] w-[32px] flex-shrink-0 items-center justify-center rounded-full border border-border bg-muted text-[12px] text-muted-foreground"
      >
        —
      </span>
      <div className="min-w-0 pt-[3px]">
        <div className="text-[13px] text-muted-foreground">—</div>
        <div className="text-[11px] text-muted-foreground">{note}</div>
      </div>
    </div>
  );
}

/** The name, as a control when the person can be opened and as text otherwise. */
function PersonName({ person, onOpen }) {
  if (!onOpen || !person.tenantId) return person.name;
  return (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        onOpen(person.tenantId);
      }}
      className="max-w-full truncate text-left font-medium text-inherit hover:underline"
    >
      {person.name}
    </button>
  );
}

export default function PersonCell({ person, extraCount = 0, onOpen }) {
  if (!person || !person.name) return <EmptyPersonCell note="Unbekannt" />;

  return (
    <div className="flex items-start gap-[10px]">
      <span
        aria-hidden="true"
        className={`flex h-[32px] w-[32px] flex-shrink-0 items-center justify-center rounded-full border text-[11px] font-semibold ${toneFor(
          person.tenantId || person.name
        )}`}
      >
        {initialsFrom(person.name)}
      </span>
      <div className="min-w-0">
        <div className="truncate text-[13px] font-medium text-foreground">
          <PersonName person={person} onOpen={onOpen} />
        </div>
        {person.email ? (
          <div className="truncate text-[11px] text-muted-foreground">
            {person.email}
          </div>
        ) : null}
        {person.phone ? (
          <div className="truncate text-[11px] text-muted-foreground">
            {person.phone}
          </div>
        ) : null}
        <div className="mt-[4px] flex flex-wrap items-center gap-[5px]">
          <RoleBadge label={person.roleLabel} />
          {extraCount > 0 ? (
            <span className="text-[10px] text-muted-foreground">
              +{extraCount} weitere
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
}
